import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosWithAuth from '../../utils/axiosWithAuth';
import { Client, ClientStatistics } from '../../types/client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// State Interface
export interface SuperAdminState {
  clients: Client[];
  currentClient: Client | null;
  statistics: ClientStatistics | null;
  loading: boolean;
  error: string | null;
  actionLoading: boolean; // For suspend/activate actions
}

const initialState: SuperAdminState = {
  clients: [],
  currentClient: null,
  statistics: null,
  loading: false,
  error: null,
  actionLoading: false,
};

// Async Thunks

/**
 * Fetch all clients with optional filters
 */
export const fetchClients = createAsyncThunk(
  'superAdmin/fetchClients',
  async (filters: {
    subscriptionStatus?: string;
    subscriptionPlan?: string;
    isActive?: boolean;
  } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters?.subscriptionStatus) params.append('subscriptionStatus', filters.subscriptionStatus);
      if (filters?.subscriptionPlan) params.append('subscriptionPlan', filters.subscriptionPlan);
      if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));

      const response = await axiosWithAuth.get(`${API_URL}/clients?${params.toString()}`);
      console.log('Clients API Response:', response.data);
      console.log('Is Array?', Array.isArray(response.data.data));
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      console.error('Fetch Clients Error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
    }
  }
);

/**
 * Fetch client statistics
 */
export const fetchStatistics = createAsyncThunk(
  'superAdmin/fetchStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.get(`${API_URL}/clients/statistics`);
      console.log('Statistics API Response:', response.data);
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      console.error('Fetch Statistics Error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch statistics');
    }
  }
);

/**
 * Fetch single client by clientId
 */
export const fetchClientById = createAsyncThunk(
  'superAdmin/fetchClientById',
  async (clientId: string, { rejectWithValue }) => {
    try {
      console.log('Fetching client from API:', `${API_URL}/clients/${clientId}`);
      const response = await axiosWithAuth.get(`${API_URL}/clients/${clientId}`);
      console.log('Client API response:', response.data);
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      console.error('Error fetching client:', error.response?.data);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch client details');
    }
  }
);

export interface ClientStoreBillSetting {
  uuid: string;
  store_id: string;
  name: string;
  billPrefix?: string;
}

export const fetchClientStores = createAsyncThunk(
  'superAdmin/fetchClientStores',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.get(`${API_URL}/clients/${clientId}/stores`);
      return response.data.data as ClientStoreBillSetting[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch client stores');
    }
  },
);

export const updateStoreBillPrefixes = createAsyncThunk(
  'superAdmin/updateStoreBillPrefixes',
  async ({ clientId, stores }: { clientId: string; stores: Array<{ storeUuid: string; billPrefix: string }> }, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.patch(`${API_URL}/clients/${clientId}/store-bill-prefixes`, { stores });
      return response.data.data as ClientStoreBillSetting[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update store bill initials');
    }
  },
);

/**
 * Create new client
 */
export const createClient = createAsyncThunk(
  'superAdmin/createClient',
  async (clientData: any, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.post(`${API_URL}/clients`, clientData);
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create client');
    }
  }
);

/**
 * Update client
 */
export const updateClient = createAsyncThunk(
  'superAdmin/updateClient',
  async ({ clientId, data }: { clientId: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.put(`${API_URL}/clients/${clientId}`, data);
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update client');
    }
  }
);

/**
 * Update client subscription
 */
export const updateSubscription = createAsyncThunk(
  'superAdmin/updateSubscription',
  async ({ clientId, data }: { clientId: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.put(`${API_URL}/clients/${clientId}/subscription`, data);
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update subscription');
    }
  }
);

/**
 * Get admin user's special permissions for a client
 */
export const fetchAdminPermissions = createAsyncThunk(
  'superAdmin/fetchAdminPermissions',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.get(`${API_URL}/clients/${clientId}/admin-permissions`);
      return response.data.data as { permissionActions: string[] };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch admin permissions');
    }
  }
);

/**
 * Update admin user's special permissions for a client
 */
export const updateAdminPermissions = createAsyncThunk(
  'superAdmin/updateAdminPermissions',
  async ({ clientId, permissionActions }: { clientId: string; permissionActions: string[] }, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.patch(`${API_URL}/clients/${clientId}/admin-permissions`, { permissionActions });
      return response.data.data as { permissionActions: string[] };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update admin permissions');
    }
  }
);

/**
 * Suspend client
 */
export const suspendClient = createAsyncThunk(
  'superAdmin/suspendClient',
  async ({ clientId, reason }: { clientId: string; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.put(`${API_URL}/clients/${clientId}/suspend`, { reason });
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to suspend client');
    }
  }
);

/**
 * Activate client
 */
export const activateClient = createAsyncThunk(
  'superAdmin/activateClient',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.put(`${API_URL}/clients/${clientId}/activate`);
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to activate client');
    }
  }
);

/**
 * Deactivate client
 */
export const deactivateClient = createAsyncThunk(
  'superAdmin/deactivateClient',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await axiosWithAuth.put(`${API_URL}/clients/${clientId}/deactivate`);
      return response.data.data; // Extract actual data from wrapper
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to deactivate client');
    }
  }
);

// Slice
const superAdminSlice = createSlice({
  name: 'superAdmin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentClient: (state) => {
      state.currentClient = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Clients
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action: PayloadAction<Client[]>) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Statistics
    builder
      .addCase(fetchStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatistics.fulfilled, (state, action: PayloadAction<ClientStatistics>) => {
        state.loading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Client By ID
    builder
      .addCase(fetchClientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientById.fulfilled, (state, action: PayloadAction<Client>) => {
        state.loading = false;
        state.currentClient = action.payload;
      })
      .addCase(fetchClientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Client
    builder
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state, action: PayloadAction<Client>) => {
        state.loading = false;
        state.clients.unshift(action.payload);
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Client
    builder
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state, action: PayloadAction<Client>) => {
        state.loading = false;
        const index = state.clients.findIndex((c) => c.clientId === action.payload.clientId);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
        if (state.currentClient?.clientId === action.payload.clientId) {
          state.currentClient = action.payload;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Subscription
    builder
      .addCase(updateSubscription.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action: PayloadAction<Client>) => {
        state.actionLoading = false;
        const index = state.clients.findIndex((c) => c.clientId === action.payload.clientId);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
        if (state.currentClient?.clientId === action.payload.clientId) {
          state.currentClient = action.payload;
        }
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });

    // Suspend Client
    builder
      .addCase(suspendClient.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(suspendClient.fulfilled, (state, action: PayloadAction<Client>) => {
        state.actionLoading = false;
        const index = state.clients.findIndex((c) => c.clientId === action.payload.clientId);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
        if (state.currentClient?.clientId === action.payload.clientId) {
          state.currentClient = action.payload;
        }
      })
      .addCase(suspendClient.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });

    // Activate Client
    builder
      .addCase(activateClient.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(activateClient.fulfilled, (state, action: PayloadAction<Client>) => {
        state.actionLoading = false;
        const index = state.clients.findIndex((c) => c.clientId === action.payload.clientId);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
        if (state.currentClient?.clientId === action.payload.clientId) {
          state.currentClient = action.payload;
        }
      })
      .addCase(activateClient.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });

    // Deactivate Client
    builder
      .addCase(deactivateClient.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deactivateClient.fulfilled, (state, action: PayloadAction<Client>) => {
        state.actionLoading = false;
        const index = state.clients.findIndex((c) => c.clientId === action.payload.clientId);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
        if (state.currentClient?.clientId === action.payload.clientId) {
          state.currentClient = action.payload;
        }
      })
      .addCase(deactivateClient.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentClient } = superAdminSlice.actions;
export default superAdminSlice.reducer;
